import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Create axios instance
const apiClient = axios.create({
  baseURL: API,
});

// Add auth header to requests if token exists
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('bormula_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ============== Public API ==============

export const getArticles = async (params = {}) => {
  const response = await apiClient.get('/articles', { params });
  return response.data;
};

export const getArticleBySlug = async (slug) => {
  const response = await apiClient.get(`/articles/${slug}`);
  return response.data;
};

export const getRelatedArticles = async (slug, limit = 3) => {
  const response = await apiClient.get(`/articles/${slug}/related`, { params: { limit } });
  return response.data;
};

export const searchArticles = async (query) => {
  const response = await apiClient.get('/articles/search', { params: { q: query } });
  return response.data;
};

export const getStats = async () => {
  const response = await apiClient.get('/stats');
  return response.data;
};

// ============== F1 Live Data ==============

export const getF1Schedule = async (season = 'current') => {
  const response = await apiClient.get('/f1/schedule', { params: { season } });
  return response.data;
};

export const getF1DriverStandings = async (season = 'current') => {
  const response = await apiClient.get('/f1/standings/drivers', { params: { season } });
  return response.data;
};

export const getF1ConstructorStandings = async (season = 'current') => {
  const response = await apiClient.get('/f1/standings/constructors', { params: { season } });
  return response.data;
};

export const getF1LatestResults = async (season = 'current') => {
  const response = await apiClient.get('/f1/results/latest', { params: { season } });
  return response.data;
};

// ============== Cloudinary ==============

export const getCloudinarySignature = async () => {
  const response = await apiClient.get('/cloudinary/signature');
  return response.data;
};

export const uploadToCloudinary = async (file) => {
  const sig = await getCloudinarySignature();
  
  const formData = new FormData();
  formData.append('file', file);
  formData.append('api_key', sig.api_key);
  formData.append('timestamp', String(sig.timestamp));
  formData.append('signature', sig.signature);
  formData.append('folder', sig.folder);
  
  const response = await axios.post(
    `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
    formData
  );
  
  return response.data;
};

// ============== Admin Auth ==============

export const adminLogin = async (username, password) => {
  const response = await apiClient.post('/admin/login', { username, password });
  localStorage.setItem('bormula_token', response.data.token);
  localStorage.setItem('bormula_username', response.data.username);
  localStorage.setItem('bormula_role', response.data.role || 'admin');
  localStorage.setItem('bormula_writer_name', response.data.writer_name || '');
  return response.data;
};

export const adminSetup = async (username, password) => {
  const response = await apiClient.post('/admin/setup', { username, password });
  return response.data;
};

export const verifyAdmin = async () => {
  const token = localStorage.getItem('bormula_token');
  if (!token) return null;
  
  try {
    const response = await apiClient.get('/admin/verify', {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch {
    localStorage.removeItem('bormula_token');
    localStorage.removeItem('bormula_username');
    localStorage.removeItem('bormula_role');
    localStorage.removeItem('bormula_writer_name');
    return null;
  }
};

export const adminLogout = () => {
  localStorage.removeItem('bormula_token');
  localStorage.removeItem('bormula_username');
  localStorage.removeItem('bormula_role');
  localStorage.removeItem('bormula_writer_name');
};

// ============== Admin Articles ==============

export const getAdminArticles = async () => {
  const token = localStorage.getItem('bormula_token');
  const response = await apiClient.get('/admin/articles', {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const createArticle = async (articleData) => {
  const token = localStorage.getItem('bormula_token');
  const response = await apiClient.post('/admin/articles', articleData, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const updateArticle = async (id, articleData) => {
  const token = localStorage.getItem('bormula_token');
  const response = await apiClient.put(`/admin/articles/${id}`, articleData, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const deleteArticle = async (id) => {
  const token = localStorage.getItem('bormula_token');
  const response = await apiClient.delete(`/admin/articles/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

// ============== Public Writers ==============

export const getWriters = async () => {
  const response = await apiClient.get('/writers');
  return response.data;
};

export const getWriterBySlug = async (slug) => {
  const response = await apiClient.get(`/writers/${slug}`);
  return response.data;
};

// ============== Admin Writers ==============

export const getAdminWriters = async () => {
  const token = localStorage.getItem('bormula_token');
  const response = await apiClient.get('/admin/writers', {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const createWriter = async (writerData) => {
  const token = localStorage.getItem('bormula_token');
  const response = await apiClient.post('/admin/writers', writerData, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const updateWriter = async (id, writerData) => {
  const token = localStorage.getItem('bormula_token');
  const response = await apiClient.put(`/admin/writers/${id}`, writerData, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

export const deleteWriter = async (id) => {
  const token = localStorage.getItem('bormula_token');
  const response = await apiClient.delete(`/admin/writers/${id}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return response.data;
};

// ============== AI Image Generation ==============

export const generateArticleImage = async (title, content) => {
  const response = await apiClient.post('/admin/generate-image', { title, content }, { timeout: 120000 });
  return response.data;
};

// ============== Writer Accounts (Admin) ==============

export const getAccounts = async () => {
  const response = await apiClient.get('/admin/accounts');
  return response.data;
};

export const createAccount = async (data) => {
  const response = await apiClient.post('/admin/accounts', data);
  return response.data;
};

export const updateAccount = async (id, data) => {
  const response = await apiClient.put(`/admin/accounts/${id}`, data);
  return response.data;
};

export const deleteAccount = async (id) => {
  const response = await apiClient.delete(`/admin/accounts/${id}`);
  return response.data;
};

export const getLoginLogs = async (limit = 100) => {
  const response = await apiClient.get('/admin/login-logs', { params: { limit } });
  return response.data;
};

// ============== Analytics (Admin) ==============

export const getAnalyticsOverview = async (period = 'week') => {
  const response = await apiClient.get('/analytics/overview', { params: { period } });
  return response.data;
};

export const getRecentVisitors = async (limit = 50) => {
  const response = await apiClient.get('/analytics/recent-visitors', { params: { limit } });
  return response.data;
};

// ============== Achievements ==============

export const getAchievements = async (activeOnly = true) => {
  const response = await apiClient.get('/achievements', { params: { active_only: activeOnly } });
  return response.data;
};

export const createAchievement = async (data) => {
  const response = await apiClient.post('/admin/achievements', data);
  return response.data;
};

export const updateAchievement = async (id, data) => {
  const response = await apiClient.put(`/admin/achievements/${id}`, data);
  return response.data;
};

export const deleteAchievement = async (id) => {
  const response = await apiClient.delete(`/admin/achievements/${id}`);
  return response.data;
};
